import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FB_MEMBERS_DISPLAY } from "@/lib/brand";
import { useSeo } from "@/hooks/useSeo";
import FaqJsonLd from "@/components/FaqJsonLd";
import { Users, HeartHandshake, Gift, Mail, Facebook, Megaphone, CalendarDays, BookOpen,
         QrCode, Ticket, ShieldCheck, Sparkles, Building2, CheckCircle2, Rocket } from "lucide-react";

const FEATURES = [
  { icon: Ticket, title: "Pass numérique gratuit pour vos membres", text: "Offrez à vos salariés, adhérents ou amicales un accès gratuit à tous les bons plans des commerces locaux partenaires." },
  { icon: Gift, title: "Un réseau d'avantages locaux", text: "Des dizaines d'offres et réductions chez les commerçants, artisans et professionnels du territoire, renouvelées régulièrement." },
  { icon: Users, title: "Gestion de vos membres", text: "Invitez, organisez et suivez vos membres depuis un espace dédié, simple et sécurisé." },
  { icon: Mail, title: "Mailing & communication", text: "Informez vos membres des nouveautés et bons plans par e-mail, avec des modèles prêts à l'emploi." },
  { icon: Facebook, title: "Groupes Facebook locaux", text: `Relayez vos actualités auprès de notre réseau de communautés Facebook locales — plus de ${FB_MEMBERS_DISPLAY} membres en cumulé.` },
  { icon: Megaphone, title: "Mises en avant", text: "Votre organisme et vos initiatives mis en avant auprès de la communauté locale." },
  { icon: BookOpen, title: "Blog & guides locaux", text: "Du contenu local utile (bons plans, actualités, guides) à partager avec vos membres." },
  { icon: CalendarDays, title: "Événements", text: "Annoncez vos événements, assemblées et temps forts et touchez facilement vos membres." },
  { icon: QrCode, title: "Diffusion simplifiée", text: "Un QR code et un lien d'invitation à partager pour que vos membres rejoignent le Pass en un instant." },
  { icon: BookOpen, title: "Ressources", text: "Guides, supports et accompagnement pour déployer le Pass auprès de votre structure." },
  { icon: Sparkles, title: "Outils IA", text: "Des outils d'aide à la rédaction pour vos communications, inclus et simples d'usage." },
  { icon: HeartHandshake, title: "100 % gratuit & local", text: "Aucun coût pour votre organisme : notre mission est de faire vivre l'économie locale d'Eure-et-Loir." },
];

const AUDIENCES = [
  { icon: Building2, label: "Comités d'entreprise (CSE)" },
  { icon: Users, label: "Associations & amicales" },
  { icon: HeartHandshake, label: "Clubs & collectivités" },
];

export default function AssociationsLanding() {
  useSeo({
    title: "Associations en Eure-et-Loir — visibilité gratuite | PROXIreduc-28",
    description: "Faites connaître votre association ou club en Eure-et-Loir : page publique, communication sur vos activités, recrutement de membres et outils numériques gratuits sur PROXIreduc-28.",
    canonicalPath: "/pour-les-associations",
    image: "/og-image.jpg",
  });
  return (
    <div className="bg-[#FAF8F5]" data-testid="associations-landing-page">
      <FaqJsonLd faq={[
        { q: "Comment faire connaître son association en Eure-et-Loir ?", a: "Créez gratuitement la page publique de votre association sur PROXIreduc-28 : présentez vos activités, publiez vos actualités et vos événements, et gagnez en visibilité locale." },
        { q: "La page association est-elle vraiment gratuite ?", a: "Oui, la présence des associations et des clubs est entièrement gratuite sur PROXIreduc-28." },
        { q: "Quels outils pour recruter des membres ?", a: "Page de présentation publique, communication sur les événements, lien d'adhésion et assistant IA pour développer votre communauté locale." },
      ]} />
      {/* HERO */}
      <section className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[10px] font-bold uppercase tracking-widest"><Users size={12}/> CSE · Clubs · Associations</div>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
            Gâtez vos membres avec des <span className="text-emerald-300">avantages locaux</span>
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl">
            Offrez gratuitement à vos salariés, adhérents ou amicales un Pass numérique donnant accès aux bons plans des commerces du territoire. Fédérez, fidélisez et faites vivre l'économie locale.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {AUDIENCES.map((a) => (
              <span key={a.label} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold"><a.icon size={13}/> {a.label}</span>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/organismes-collectivites/inscription"><Button className="bg-white text-emerald-900 hover:bg-white/90 h-12 px-6 font-extrabold" data-testid="orgs-cta-create"><Rocket size={16} className="mr-2"/> Créer mon espace organisme — gratuit</Button></Link>
            <Link to="/organismes-collectivites"><Button variant="outline" className="h-12 px-6 border-white/40 text-white hover:bg-white/10" data-testid="orgs-cta-details">En savoir plus</Button></Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Tout ce que nous apportons à votre organisme</h2>
        <p className="mt-2 text-slate-600 text-sm md:text-base">Un bouquet d'avantages et d'outils, entièrement gratuit, pour animer votre communauté.</p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3 card-hover" data-testid="orgs-feature">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0"><f.icon size={20}/></div>
              <div>
                <div className="font-heading font-bold text-slate-900">{f.title}</div>
                <p className="mt-1 text-sm text-slate-600">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* GARANTIES */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 md:p-8 grid sm:grid-cols-3 gap-4">
          {["100 % gratuit pour votre organisme", "Aucun engagement", "Fédère et fidélise vos membres"].map((t) => (
            <div key={t} className="flex items-start gap-2 text-sm text-slate-800"><ShieldCheck size={18} className="text-emerald-700 shrink-0 mt-0.5"/> {t}</div>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-emerald-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-extrabold">Offrez des avantages locaux à vos membres</h2>
          <p className="mt-3 text-white/80">Créez gratuitement votre espace organisme en quelques minutes.</p>
          <Link to="/organismes-collectivites/inscription"><Button className="mt-6 bg-white text-emerald-900 hover:bg-white/90 h-12 px-6 text-base font-extrabold" data-testid="orgs-final-cta"><Rocket size={18} className="mr-2"/> Créer mon espace organisme — gratuit</Button></Link>
          <div className="mt-3 text-xs text-white/70 inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-300"/> 100 % gratuit · sans engagement</div>
        </div>
      </section>
    </div>
  );
}
